import { RequestStatus } from 'typed/api';
import { Entity, NetworkArray } from 'typed/api/entities';
import { IAction } from './action';
import networkAction, { NetworkAction } from './networkAction';
import { EntityActionMap, IEntityActionCreatorSet, InferredActionType, PartialEntity, ProvidedKeys } from './entityActionSet';

export interface NestedEntityActionCreatorSet<T extends string, P extends Entity, E extends Entity> extends IEntityActionCreatorSet<T, E> {
    loadAll(parentID: P['ID'], ...options: unknown[]/* TODO infer from corresponding API method */): NetworkAction<T, { parentID: P['ID']; }, NetworkArray<E>>;
    load(parentID: P['ID'], entityID: E['ID'], ...options: unknown[]/* TODO infer from corresponding API method */): NetworkAction<T, { parentID: P['ID']; entityID: E['ID']; }, E>;
    addAll(parentID: P['ID'], entities: PartialEntity<E>[]): NetworkAction<T, { parentID: P['ID']; entities: PartialEntity<E>[]; }, void>;
    addAll(parentID: P['ID'], entities: PartialEntity<E>[], sync: true, reloadAllAfterSync?: boolean, ...options: unknown[]): NetworkAction<T, { parentID: P['ID']; entities: PartialEntity<E>[]; }, number[]>;
    add(parentID: P['ID'], entity: PartialEntity<E>): NetworkAction<T, { parentID: P['ID']; entity: PartialEntity<E>; }, void>;
    add(parentID: P['ID'], entity: PartialEntity<E>, sync: true, reloadAfterSync?: boolean): NetworkAction<T, { parentID: P['ID']; entity: PartialEntity<E>; }, number>;
    editAll(parentID: P['ID'], entities: PartialEntity<E>[], sync?: boolean, reloadAllAfterSync?: boolean, ...options: unknown[]): NetworkAction<T, { parentID: P['ID']; entities: PartialEntity<E>[]; }, void>;
    edit(parentID: P['ID'], entity: PartialEntity<E>, sync?: boolean, reloadAfterSync?: boolean): NetworkAction<T, { parentID: P['ID']; entity: PartialEntity<E>; }, void>;
    deleteAll(parentID: P['ID'], entities: E[]): NetworkAction<T, { parentID: P['ID']; entityIDs: E['ID'][]; }, void>;
    delete(parentID: P['ID'], entity: E, sync?: boolean): NetworkAction<T, { parentID: P['ID']; entityID: E['ID']; }, void>;
    voidAll(parentID: P['ID'], immediately?: true): IAction<T, { parentID: P['ID']; immediately: boolean; }>;
    void(parentID: P['ID'], entityID: E['ID'], immediately?: true): IAction<T, { parentID: P['ID']; immediately: boolean; entityID: E['ID']; }>;
}

export type NestedEntityMappedActionCreators<P extends Entity, E extends Entity, M extends EntityActionMap<any, E>> =
    { readonly [K in ProvidedKeys<M>]: NestedEntityActionCreatorSet<InferredActionType<M, K>, P, E>[K] };

/**
 * @param parent a dummy parameter used to properly guess entity type, should be removed once API methods have proper types
 * @param entity a dummy parameter used to properly guess entity type, should be removed once API methods have proper types
  */
export default function nestedEntityActionSet<P extends Entity, E extends Entity, M extends EntityActionMap<any, E>>(_parent: P, _entity: E, map: M): { readonly [K in ProvidedKeys<M>]: NestedEntityActionCreatorSet<InferredActionType<M, K>, P, E>[K] } {
    // The return type is actually MappedActionCreators<E, M>, but if result is declared this way, type hints/intellisense just show the name, which is not so helpful.
    // Explicit declaration here causes nice hints of all available methods, overloads etc.

    const
        addAllType = map.addAll && map.addAll.type!,
        addAllMethod = map.addAll && map.addAll.method!,
        addAllAction: (<T extends string, P>(parentID: number, entities: PartialEntity<E>[]) => NetworkAction<T, P, number[]>) | null = addAllType && addAllMethod ?
            (parentID, entities) => networkAction(
                addAllType, { parentID, entityIDs: entities.map(({ ID }) => ID), entities }, addAllMethod, entities
            ) :
            null,

        addType = map.add && map.add.type!,
        addMethod = map.add && map.add.method!,
        addAction: (<T extends string, P>(parentID: number, entity: Partial<E>) => NetworkAction<T, P, number>) | null = addType && addMethod ?
            (parentID, entity) => networkAction(
                addType, { parentID, entityID: entity.ID, entity }, addMethod, entity
            ) :
            null,


        result = {} as unknown as NestedEntityActionCreatorSet<string, P, E>;

    if (map.loadAll) {
        const
            type = map.loadAll.type!,
            method = map.loadAll.method!;

        result.loadAll = (parentID, ...options) => networkAction(type, { parentID }, method, ...options);
    }

    if (map.load) {
        const
            type = map.load.type!,
            method = map.load.method!;

        result.load = (parentID, entityID, ...options) => networkAction(
            type, { parentID, entityID }, method, entityID, ...options
        );
    }

    if (map.addAll) {
        if (!addAllType || !addAllAction) {
            throw new Error('Can\'t happen.');
        }

        (result as any).addAll = (parentID: P['ID'], entities: PartialEntity<E>[], sync = false, reloadAllAfterSync = false, ...reloadAllOptions: unknown[]): NetworkAction<typeof addAllType, any, any> =>
            async(dispatch) => {
                dispatch({
                    type: addAllType,
                    payload: { parentID, entityIDs: entities.map(({ ID }) => ID), entities, status: RequestStatus.UNSYNCED }
                });

                if (!sync) {
                    return entities.map(({ ID }) => ID);
                }
                else if (!reloadAllAfterSync || !DEBUG && !result.loadAll) {
                    return await dispatch(addAllAction(parentID, entities)) as any;
                }
                else {
                    if (result.loadAll) {
                        await dispatch(addAllAction(parentID, entities));
                        return await dispatch(result.loadAll(parentID, ...reloadAllOptions));
                    }

                    throw new Error('Can\'t reloadAll after sync - methodsMap.loadAll is not configured.');
                }
            };
    }


    if (map.editAll) {
        const
            type = map.editAll.type!,
            method = map.editAll.method!,
            editAllAction = (parentID: P['ID'], entities: PartialEntity<E>[]) => networkAction(
                type, { parentID, entityIDs: entities.map(({ ID }) => ID), entities }, method, entities
            );

        (result as any).editAll = (parentID: P['ID'], entities: PartialEntity<E>[], sync = false, reloadAllAfterSync = false, ...reloadAllOptions: unknown[]): NetworkAction<typeof type, any, any> =>
            async(dispatch) => {
                dispatch({
                    type,
                    payload: { parentID, entityIDs: entities.map(({ ID }) => ID), entities, status: RequestStatus.UNSYNCED }
                });

                if (!sync) {
                    return undefined;
                }
                else if (!reloadAllAfterSync || !DEBUG && !result.loadAll) {
                    return await dispatch(editAllAction(parentID, entities));
                }
                else {
                    if (result.loadAll) {
                        await dispatch(editAllAction(parentID, entities));
                        return await dispatch(result.loadAll(parentID, ...reloadAllOptions));
                    }


                    throw new Error('Can\'t reloadAll after sync - methodsMap.loadAll is not configured.');
                }
            };
    }

    if (map.add) {
        if (!addType || !addAction) {
            throw new Error('Can\'t happen.');
        }

        (result as any).add = (parentID: P['ID'], entity: PartialEntity<E>, sync = false, reloadAfterSync = false, ...reloadOptions: unknown[]): NetworkAction<typeof addType, any, any> =>
            async(dispatch) => {
                dispatch({
                    type: addType,
                    payload: { parentID, entityID: entity.ID, entity, status: RequestStatus.UNSYNCED }
                });

                if (!sync) {
                    return entity.ID;
                }
                else if (!reloadAfterSync || !DEBUG && !result.load) {
                    return await dispatch(addAction(parentID, entity));
                }
                else {
                    if (result.load) {
                        const newID = await dispatch(addAction(parentID, entity));
                        return await dispatch(result.load(parentID, newID!, ...reloadOptions));
                    }

                    throw new Error('Can\'t reload after sync - methodsMap.load is not configured.');
                }
            };
    }

    if (map.edit) {
        const
            type = map.edit.type!,
            method = map.edit.method!,

            editAction = (parentID: P['ID'], entity: PartialEntity<E>) => networkAction(
                type, { parentID, entityID: entity.ID, entity }, method, entity
            );

        (result as any).edit = (parentID: P['ID'], entity: PartialEntity<E>, sync = false, reloadAfterSync = false, ...reloadOptions: unknown[]): NetworkAction<typeof type, any, any> =>
            async(dispatch) => {
                dispatch({
                    type,
                    payload: { parentID, entityID: entity.ID, entity, status: RequestStatus.UNSYNCED }
                });

                const syncAction =
                    !entity.Custom || entity.Custom.status === RequestStatus.UNSYNCED ?
                        addAction as unknown as typeof editAction :
                        editAction;

                if (!sync) {
                    return undefined;
                }
                else if (!reloadAfterSync || !DEBUG && !result.load) {
                    return await dispatch(syncAction(parentID, entity));
                }
                else {
                    if (result.load) {
                        await dispatch(syncAction(parentID, entity));
                        return await dispatch(result.load(parentID, entity.ID, ...reloadOptions));
                    }

                    throw new Error('Can\'t reload after sync - methodsMap.load is not configured.');
                }
            };
    }

    if (map.deleteAll) {
        const
            type = map.deleteAll.type!,
            method = map.deleteAll.method!;

        // TODO this code works if entity successfully synced or not synced at all
        // but it will fail to delete entity if sync request failed or not yet finished
        // latter situation is somewhat fixable by building request queue
        // but how to delete entity from DB if it has been added but its new ID has been lost in network failure?
        result.deleteAll = (parentID, entities) => async(dispatch) => {
            const
                localEntityIDs = entities.filter(({ Custom: { status } }) => status === RequestStatus.UNSYNCED).map(({ ID }) => ID),
                remoteEntityIDs = entities.filter(({ Custom: { status } }) => status !== RequestStatus.UNSYNCED).map(({ ID }) => ID);

            if (localEntityIDs.length) {
                dispatch({
                    type,
                    payload: { status: RequestStatus.SUCCEEDED, parentID, entityIDs: localEntityIDs, response: undefined }
                });
            }
            if (remoteEntityIDs.length) {
                await dispatch(networkAction(type, { parentID, entityIDs: remoteEntityIDs }, method, remoteEntityIDs));
            }
        };
    }

    if (map.delete) {
        const
            type = map.delete.type!,
            method = map.delete.method!;

        // TODO this code works if entity successfully synced or not synced at all
        // but it will fail to delete entity if sync request failed or not yet finished
        // latter situation is somewhat fixable by building request queue
        // but how to delete entity from DB if it has been added but its new ID has been lost in network failure?
        result.delete = (parentID, entity, sync = true) => async(dispatch) => {
            if (!sync || entity.Custom.status === RequestStatus.UNSYNCED) {
                dispatch({
                    type,
                    payload: { status: RequestStatus.SUCCEEDED, parentID, entityID: entity.ID, response: undefined }
                });
            }
            else {
                await dispatch(networkAction(type, { parentID, entityID: entity.ID }, method, entity.ID));
            }
        };
    }

    if (map.voidAll) {
        const { type } = map.voidAll;
        result.voidAll = (parentID, immediately = true) => ({
            type,
            payload: { parentID, immediately }
        });
    }

    if (map.void) {
        const { type } = map.void;
        result.void = (parentID, entityID, immediately = true) => ({
            type,
            payload: { parentID, entityID, immediately }
        });
    }

    return result as unknown as NestedEntityMappedActionCreators<P, E, M>;
}

